import { FC, Fragment, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDidMount, useEffectOnceWhen, useWillUnmount } from "rooks";
import { Box, CircularProgress, Stack } from "@mui/material";
import {
  AutoFixHighOutlined,
  NotificationsActiveOutlined,
} from "@mui/icons-material";
import { WorkflowForm } from "./WorkflowForm";
import { PageHeader } from "../../../common/PageHeader";
import { workflowsActionSearchParamKey } from "../data-grid/utils/constants/constants";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resetAutomationsState } from "@/store/automations/automationsSlice";
import { currentEditingWorkflowLoadingSelector } from "@/store/automations/selectros/workflow/data/edit/currentEditingWorkflowLoadingSelector";
import { editingWorkflowIdIsWrongSelector } from "@/store/automations/selectros/workflow/data/edit/editingWorkflowIdIsWrongSelector";
import { initiateWorkflowThunk } from "@/store/automations/thunks/wokrflows/initiate/initiateWorkflowThunk";
import { getProvidersRegionsThunk } from "@/store/automations/thunks/regions/getProvidersRegionsThunk";
import { ScheduleActionType } from "@/services/cloudchipr.api";
import { workflowActionSelector } from "@/store/automations/selectros/workflow/workflowActionSelector";

export const SingleWorkflow: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const actionType = searchParams.get(
    workflowsActionSearchParamKey,
  ) as ScheduleActionType | null;
  const action = useAppSelector(workflowActionSelector);

  const dispatch = useAppDispatch();

  const loading = useAppSelector(currentEditingWorkflowLoadingSelector);
  const error = useAppSelector(editingWorkflowIdIsWrongSelector);

  const breadcrumbs = useMemo(() => {
    const title = `${id ? "Edit" : "Create"} ${action === "notify" ? "Notify Only" : "Take Action"} Workflow`;

    return [
      { title: "Workflows", to: "/automations/workflow" },
      {
        title,
        icon:
          action === "notify" ? (
            <NotificationsActiveOutlined fontSize="small" />
          ) : (
            <AutoFixHighOutlined fontSize="small" />
          ),
      },
    ];
  }, [id, action]);

  useEffectOnceWhen(() => {
    navigate("/automations/workflow", { replace: true });
  }, error);

  useDidMount(async () => {
    await dispatch(getProvidersRegionsThunk());
    dispatch(initiateWorkflowThunk({ id, action: actionType ?? "notify" }));
  });

  useWillUnmount(() => {
    dispatch(resetAutomationsState());
  });

  return (
    <Fragment>
      <PageHeader breadcrumbs={breadcrumbs} />

      <Box bgcolor="white" flex={1}>
        {loading ? (
          <Stack alignItems="center" pt={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <WorkflowForm />
        )}
      </Box>
    </Fragment>
  );
};
